import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList } from "@/components/ui/tabs";

export const PhoneNumberDetailsSkeleton = () => {
    return (
        <div className="flex flex-col h-full">
            {/* SiteHeader Skeleton */}
            <div className="flex h-14 items-center gap-4 border-b bg-background px-4 lg:px-6">
                <div className="flex items-center gap-2">
                    <Skeleton className="h-9 w-16" /> {/* Back button */}
                </div>
                <div className="flex flex-col gap-1">
                    <Skeleton className="h-4 w-40" /> {/* Title */}
                    <Skeleton className="h-3 w-56" /> {/* Subtitle */}
                </div>
                <div className="ml-auto flex items-center gap-2">
                    <Skeleton className="h-9 w-20" /> {/* Delete button */}
                    <Skeleton className="h-9 w-32" /> {/* Save/Start Call button */}
                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-auto px-4 py-6 md:px-8 md:py-8">
                <div className="space-y-6">
                    {/* SIP Info Cards Skeleton */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Card className="border bg-muted/30">
                            <div className="p-4 space-y-3">
                                <Skeleton className="h-4 w-32" />
                                <Skeleton className="h-3 w-48" />
                                <Skeleton className="h-9 w-full rounded-md" />
                            </div>
                        </Card>
                    </div>

                    <Tabs defaultValue="inbound" className="space-y-6">
                        <TabsList className="h-11 w-full max-w-md">
                            <Skeleton className="h-9 flex-1 rounded-md" />
                            <Skeleton className="h-9 flex-1 rounded-md" />
                        </TabsList>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Basic Information Card Skeleton - Full Width */}
                            <Card className="shadow-sm lg:col-span-2">
                                <CardHeader className="border-b bg-muted/20">
                                    <div className="flex items-center gap-3">
                                        <Skeleton className="h-8 w-8 rounded-lg" />
                                        <div className="flex-1 space-y-1">
                                            <Skeleton className="h-4 w-32" />
                                            <Skeleton className="h-3 w-56" />
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="p-6 space-y-6">
                                    <div className="grid md:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <Skeleton className="h-4 w-28" />
                                            <Skeleton className="h-9 w-full" />
                                        </div>
                                        <div className="space-y-2">
                                            <Skeleton className="h-4 w-16" />
                                            <Skeleton className="h-9 w-full" />
                                        </div>
                                    </div>
                                    <div className="grid md:grid-cols-4 gap-6">
                                        <div className="md:col-span-2 space-y-2">
                                            <Skeleton className="h-4 w-28" />
                                            <Skeleton className="h-9 w-full" />
                                        </div>
                                        <div className="space-y-2">
                                            <Skeleton className="h-4 w-20" />
                                            <Skeleton className="h-9 w-full" />
                                        </div>
                                        <div className="space-y-2">
                                            <Skeleton className="h-4 w-20" />
                                            <Skeleton className="h-9 w-full" />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Server Configuration Card Skeleton - Full Width */}
                            <Card className="shadow-sm lg:col-span-2">
                                <CardHeader className="border-b bg-muted/20">
                                    <div className="flex items-center gap-3">
                                        <Skeleton className="h-8 w-8 rounded-lg" />
                                        <div className="flex-1 space-y-1">
                                            <Skeleton className="h-4 w-40" />
                                            <Skeleton className="h-3 w-56" />
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="p-6 space-y-6">
                                    <div className="grid md:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <Skeleton className="h-4 w-24" />
                                            <Skeleton className="h-9 w-full" />
                                        </div>
                                        <div className="space-y-2">
                                            <Skeleton className="h-4 w-32" />
                                            <Skeleton className="h-9 w-full" />
                                            <Skeleton className="h-3 w-2/3" />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Call Routing Card Skeleton - Left Column */}
                            <Card className="shadow-sm">
                                <CardHeader className="border-b bg-muted/20">
                                    <div className="flex items-center gap-3">
                                        <Skeleton className="h-8 w-8 rounded-lg" />
                                        <div className="flex-1 space-y-1">
                                            <Skeleton className="h-4 w-24" />
                                            <Skeleton className="h-3 w-64" />
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="p-6 space-y-6">
                                    <div className="space-y-2">
                                        <Skeleton className="h-4 w-20" />
                                        <Skeleton className="h-9 w-full" />
                                    </div>
                                    <div className="space-y-2">
                                        <Skeleton className="h-4 w-24" />
                                        <Skeleton className="h-9 w-full" />
                                        <Skeleton className="h-3 w-3/4" />
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Call Forwarding Card Skeleton - Right Column */}
                            <Card className="shadow-sm">
                                <CardHeader className="border-b bg-muted/20">
                                    <div className="flex items-center gap-3">
                                        <Skeleton className="h-8 w-8 rounded-lg" />
                                        <div className="flex-1 space-y-1">
                                            <Skeleton className="h-4 w-28" />
                                            <Skeleton className="h-3 w-64" />
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="p-6 space-y-6">
                                    <div className="space-y-2">
                                        <Skeleton className="h-4 w-36" />
                                        <Skeleton className="h-9 w-full" />
                                        <Skeleton className="h-3 w-2/3" />
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </Tabs>
                </div>
            </div>
        </div>
    );
};
